import { bytechatUrl } from '../utils/network'; // Import getBaseUrl

/**
 * Uploads a file to the server.
 * @param {File} file - The file to upload.
 * @returns {Promise<Object>} - A promise that resolves with a generic success message.
 */
export const uploadFileApi = async (file) => {
  const BASE_URL = await bytechatUrl(); // Get base URL dynamically
  const formData = new FormData();
  formData.append('file', file);

  const uploadUrl = `${BASE_URL}files/upload/`;

  try {
    const response = await fetch(uploadUrl, {
      method: 'POST',
      body: formData,
      mode: 'no-cors', // Add this line
      headers: {
        // 'Accept': 'application/json', // With 'no-cors', 'Accept' might be restricted or ignored.
                                        // The browser controls headers more strictly.
      },
    });

    // IMPORTANT: With mode: 'no-cors', the response object is "opaque".
    // You CANNOT read response.status, response.ok, response.headers, or response.body.
    // response.ok will be false, response.status will be 0.
    // So, we can't check response.ok or parse response.json() here.
    // We assume success if no network error was thrown by fetch itself.
    // This is a "fire and forget" scenario.

    // console.log('Response with no-cors (opaque):', response); // For debugging, to see the opaque response

    return { message: "File upload request sent. Status unknown due to no-cors mode." }; // Return a generic success message

  } catch (error) {
    // This will catch network errors (e.g., server down), but not HTTP errors like 4xx/5xx
    // because the response is opaque.
    console.error('Error uploading file (network or fetch issue):', error);
    throw error; // Re-throw the error to be caught by the caller
  }
};

/**
 * Sends a chat message to the AI and gets the response.
 * If payload.stream is true, this function becomes an async generator,
 * yielding each processed chunk of the stream.
 * @param {Object} payload - The JSON payload to send.
 *   Example: { model: "gpt-4.1", messages: [...], stream: true }
 * @returns {Promise<Object>|AsyncGenerator<Object>} - A promise resolving to the server's JSON response if not streaming,
 *                                                   or an async generator yielding stream chunks if payload.stream is true.
 */
export async function* sendChatMessageApi(payload) {
  const BASE_URL = await bytechatUrl(); // Get base URL dynamically
  const chatUrl = `${BASE_URL}ai/chat/completions`; // Ensure no trailing slash if server expects none
  const apiKey = 'test-key'; // Your API key

  const response = await fetch(chatUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.text();
    throw new Error(errorData || `HTTP error! status: ${response.status}`);
  }

  if (response.body) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process buffer line by line for SSE
        let eventEndIndex;
        // SSE events are typically separated by double newlines, or a single newline if it's the end of a data chunk.
        // We'll look for single newlines to process each line.
        while ((eventEndIndex = buffer.indexOf('\n')) !== -1) {
          const line = buffer.substring(0, eventEndIndex);
          buffer = buffer.substring(eventEndIndex + 1);

          if (line.startsWith('data:')) {
            const eventData = line.substring(5); // Remove 'data:' prefix, do NOT trim
            // Yield data as is. UI concatenation + ReactMarkdown with pre-wrap will handle rendering.
            yield { rawText: eventData }; 
          } else if (line.trim() === '') {
            // Empty line might signify end of a multi-line event or just keep-alive, ignore for now
          } else {
            // console.log('SSE line (not data):', line); // For debugging other SSE lines like 'event:' or 'id:'
          }
        }
      }

      // Process any remaining buffer content
      if (buffer.startsWith('data:')) {
        const eventData = buffer.substring(5); // Remove 'data:' prefix, do NOT trim
        yield { rawText: eventData };
      }
    } finally {
      reader.releaseLock();
    }
  } else {
    throw new Error("ReadableStream not available for streaming response.");
  }
}

/**
 * Fetches the list of S3 files.
 * @returns {Promise<Array<Object>>} - A promise that resolves with the list of files.
 */
export const listS3FilesApi = async () => {
  const BASE_URL = await bytechatUrl(); // Get base URL dynamically
  const listFilesUrl = `${BASE_URL}files/list-s3-files/`;
  const apiKey = 'test-key'; // Your API key

  try {
    const response = await fetch(listFilesUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to list S3 files and parse error response.' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json(); // Assuming the API returns an array of file objects
  } catch (error) {
    console.error('Error listing S3 files:', error);
    throw error;
  }
};

/**
 * Deletes a specific S3 file.
 * @param {string} fileKey - The key of the file to delete.
 * @returns {Promise<Object>} - A promise that resolves with the server's response.
 */
export const deleteS3FileApi = async (fileKey) => {
  const BASE_URL = await bytechatUrl(); // Get base URL dynamically
  const deleteFileUrl = `${BASE_URL}files/delete-s3-file/${encodeURIComponent(fileKey)}`;
  const apiKey = 'test-key'; // Your API key

  try {
    const response = await fetch(deleteFileUrl, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
      },
    });

    if (!response.ok) {
      // Try to parse error response, otherwise use status text
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        // If response is not JSON or empty
        errorData = { message: response.statusText || 'Failed to delete file and parse error response.' };
      }
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    // If response is OK but no content (204 No Content is common for DELETE)
    if (response.status === 204) {
        return { message: `File "${fileKey}" deleted successfully.` };
    }

    return response.json(); // Or handle based on what your API returns on successful delete
  } catch (error) {
    console.error(`Error deleting S3 file "${fileKey}":`, error);
    throw error;
  }
};

/**
 * Fetches a presigned URL for downloading a specific S3 file.
 * @param {string} filename - The name of the file to download.
 * @returns {Promise<Object>} - A promise that resolves with an object containing the presigned URL.
 *                              e.g., { presigned_url: "..." }
 */
export const getS3PresignedUrlApi = async (filename) => {
  const BASE_URL = await bytechatUrl(); // Get base URL dynamically
  const presignedUrlEndpoint = `${BASE_URL}files/get-s3-presigned-url/${encodeURIComponent(filename)}`;
  const apiKey = 'test-key'; // Your API key

  try {
    const response = await fetch(presignedUrlEndpoint, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `Failed to get presigned URL for "${filename}" and parse error response.` }));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }
    return response.json(); // Expects { "presigned_url": "..." }
  } catch (error) {
    console.error(`Error fetching presigned URL for "${filename}":`, error);
    throw error;
  }
};

/**
 * Fetches the processing status of files.
 * @returns {Promise<Array<Object>>} - A promise that resolves with a list of file statuses.
 *                                      Example: [{ filename: "doc.pdf", status: "pending" }, ...]
 */
export const getFileProcessingStatusApi = async () => {
  const BASE_URL = await bytechatUrl();
  // Assuming the endpoint is /files/status relative to the bytechatUrl
  const statusUrl = `${BASE_URL}files/status`; 
  const apiKey = 'test-key'; // Assuming same API key usage

  try {
    const response = await fetch(statusUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${apiKey}`, // Add Authorization if required by this endpoint
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch file statuses and parse error response.' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    return response.json(); // Expects an array of file status objects
  } catch (error) {
    console.error('Error fetching file processing statuses:', error);
    throw error;
  }
};

/**
 * Fetches the list of scraped URLs.
 * @returns {Promise<Array<Object>>} - A promise that resolves with the list of scraped URLs.
 *                                      Example: [{ id: "1", url: "http://example.com", title: "Example Domain" }, ...]
 */
export const listScrapedUrlsApi = async () => {
  const BASE_URL = await bytechatUrl();
  const scrapeListUrl = `${BASE_URL}webscraper/scrape`; // Assuming this is the correct endpoint
  const apiKey = 'test-key'; // Assuming same API key usage

  try {
    const response = await fetch(scrapeListUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${apiKey}`, // Add Authorization if required
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch scraped URLs and parse error response.' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    return response.json(); // Expects an array of scraped URL objects
  } catch (error) {
    console.error('Error fetching scraped URLs:', error);
    throw error;
  }
};

/**
 * Sends a URL to be scraped by the webscraper.
 * @param {string} urlToScrape - The URL to be scraped.
 * @returns {Promise<Object>} - A promise that resolves with the server's response.
 */
export const scrapeUrlApi = async (urlToScrape) => {
  const BASE_URL = await bytechatUrl();
  const scrapePostUrl = `${BASE_URL}webscraper/scrape/`; // Ensure trailing slash if required by backend
  const apiKey = 'test-key'; // Assuming same API key usage

  try {
    const response = await fetch(scrapePostUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${apiKey}`, // Add Authorization if required
      },
      body: JSON.stringify({ url: urlToScrape }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to scrape URL and parse error response.' }));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }
    // If response is OK but no content (e.g., 202 Accepted or 201 Created without body)
    if (response.status === 202 || response.status === 201 || response.status === 204) {
        return { message: `Scraping request for "${urlToScrape}" accepted.` };
    }
    return response.json(); // Expects a response object, e.g., { message: "Scraping started", task_id: "..." }
  } catch (error) {
    console.error(`Error scraping URL "${urlToScrape}":`, error);
    throw error;
  }
};
